import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useCreateQuote, useUpdateQuote } from "../../api/quotes";
import { useCustomers } from "../../api/customers";
import { useSkillLevels } from "../../api/skillLevels";
import { useDebounce } from "../../hooks/useDebounce";
import { useToast } from "../../components/ToastProvider";
import { formatBRL } from "../../utils/money";
import { Quote } from "../../types/dtos";

type Row = { description: string; skillLevelId: string; hours: string };

const BLANK_ROW: Row = { description: "", skillLevelId: "", hours: "" };

export function NewQuoteModal({ show, editing, onClose }: { show: boolean; editing: Quote | null; onClose: () => void }) {
  const [mode, setMode] = useState<"customer" | "lead">("lead");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [leadName, setLeadName] = useState("");
  const [leadContact, setLeadContact] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState<Row[]>([{ ...BLANK_ROW }]);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(customerQuery);
  const { data: customersData } = useCustomers({ pageSize: 20, q: debouncedQuery || undefined });
  const { data: skillLevels } = useSkillLevels(false);
  const create = useCreateQuote();
  const update = useUpdateQuote();
  const toast = useToast();

  useEffect(() => {
    if (!show) return;
    if (editing) {
      setMode(editing.customer ? "customer" : "lead");
      setCustomerId(editing.customer ? String(editing.customer.id) : "");
      setCustomerQuery("");
      setLeadName(editing.leadName ?? "");
      setLeadContact(editing.leadContact ?? "");
      setNotes(editing.notes ?? "");
      setRows(
        editing.items.map((i) => ({ description: i.description, skillLevelId: String(i.skillLevelId), hours: String(i.hours) }))
      );
    } else {
      setMode("lead");
      setCustomerId("");
      setCustomerQuery("");
      setLeadName("");
      setLeadContact("");
      setNotes("");
      setRows([{ ...BLANK_ROW }]);
    }
    setError(null);
  }, [show, editing]);

  function updateRow(index: number, patch: Partial<Row>) {
    setRows((cur) => cur.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((cur) => [...cur, { ...BLANK_ROW }]);
  }

  function removeRow(index: number) {
    setRows((cur) => (cur.length > 1 ? cur.filter((_, i) => i !== index) : cur));
  }

  function rateFor(skillLevelId: string): number {
    return Number(skillLevels?.find((l) => String(l.id) === skillLevelId)?.hourlyRate ?? 0);
  }

  const total = rows.reduce((acc, r) => acc + (Number(r.hours.replace(",", ".")) || 0) * rateFor(r.skillLevelId), 0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "customer" && !customerId) return setError("Selecione o cliente.");
    if (mode === "lead" && !leadName.trim()) return setError("Informe o nome do lead.");
    const items = rows.map((r) => ({
      description: r.description.trim(),
      skillLevelId: Number(r.skillLevelId),
      hours: Number(r.hours.replace(",", ".")),
    }));
    if (items.some((i) => !i.description)) return setError("Informe a descrição de todos os itens.");
    if (items.some((i) => !i.skillLevelId)) return setError("Selecione o nível de habilidade em todos os itens.");
    if (items.some((i) => !i.hours || i.hours <= 0)) return setError("Informe horas válidas em todos os itens.");

    const input = {
      customerId: mode === "customer" ? Number(customerId) : undefined,
      leadName: mode === "lead" ? leadName : undefined,
      leadContact: mode === "lead" ? leadContact : undefined,
      notes: notes || undefined,
      items,
    };

    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, input });
        toast(`${editing.code} atualizado.`);
      } else {
        await create.mutateAsync(input);
        toast("Orçamento criado.");
      }
      onClose();
    } catch {
      setError("Não foi possível salvar o orçamento.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show={show} onHide={onClose} centered className="nt-dialog" size="lg">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>
            {editing ? `Editar ${editing.code}` : "Novo orçamento"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <div className="d-flex gap-3">
            <div className="form-check">
              <input className="form-check-input" type="radio" id="mode-lead" checked={mode === "lead"} onChange={() => setMode("lead")} />
              <label className="form-check-label" htmlFor="mode-lead" style={{ fontSize: 13 }}>
                Lead (ainda não é cliente)
              </label>
            </div>
            <div className="form-check">
              <input className="form-check-input" type="radio" id="mode-customer" checked={mode === "customer"} onChange={() => setMode("customer")} />
              <label className="form-check-label" htmlFor="mode-customer" style={{ fontSize: 13 }}>
                Cliente cadastrado
              </label>
            </div>
          </div>

          {mode === "lead" ? (
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <label className="form-label" style={{ fontSize: 12 }}>
                  Nome do lead
                </label>
                <input className="form-control" value={leadName} onChange={(e) => setLeadName(e.target.value)} />
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label" style={{ fontSize: 12 }}>
                  Contato
                </label>
                <input className="form-control" placeholder="E-mail ou telefone" value={leadContact} onChange={(e) => setLeadContact(e.target.value)} />
              </div>
            </div>
          ) : (
            <div>
              <label className="form-label" style={{ fontSize: 12 }}>
                Cliente
              </label>
              <input className="form-control mb-2" placeholder="Buscar cliente…" value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} />
              <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Selecione…</option>
                {customersData?.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="form-label mb-2" style={{ fontSize: 12, fontWeight: 700 }}>
              Itens do orçamento
            </label>
            <div className="d-flex flex-column gap-2">
              {rows.map((row, index) => (
                <div key={index} className="d-flex align-items-end gap-2 flex-wrap">
                  <div style={{ flex: 3, minWidth: 160 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>
                      Descrição
                    </label>
                    <input
                      className="form-control form-control-sm"
                      value={row.description}
                      onChange={(e) => updateRow(index, { description: e.target.value })}
                      placeholder="Ex.: Módulo de pedidos online"
                    />
                  </div>
                  <div style={{ flex: 2, minWidth: 140 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>
                      Nível
                    </label>
                    <select className="form-select form-select-sm" value={row.skillLevelId} onChange={(e) => updateRow(index, { skillLevelId: e.target.value })}>
                      <option value="">Selecione…</option>
                      {skillLevels?.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({formatBRL(l.hourlyRate)}/h)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ width: 90 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>
                      Horas
                    </label>
                    <input className="form-control form-control-sm" value={row.hours} onChange={(e) => updateRow(index, { hours: e.target.value })} placeholder="0" />
                  </div>
                  <div style={{ width: 100, fontSize: 12.5, fontWeight: 700, paddingBottom: 6 }} className="nt-tabular">
                    {formatBRL((Number(row.hours.replace(",", ".")) || 0) * rateFor(row.skillLevelId))}
                  </div>
                  <button type="button" className="btn btn-ghost text-danger" style={{ fontSize: 12, paddingBottom: 6 }} onClick={() => removeRow(index)}>
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-ghost mt-2" style={{ fontSize: 12.5 }} onClick={addRow}>
              + Adicionar item
            </button>
          </div>

          <div>
            <label className="form-label" style={{ fontSize: 12 }}>
              Observações
            </label>
            <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="p-2 d-flex justify-content-between align-items-center" style={{ background: "var(--sp-grey-050)", borderRadius: "var(--radius-md)" }}>
            <span style={{ fontSize: 13 }}>Total do orçamento</span>
            <strong className="nt-tabular" style={{ fontSize: 16 }}>
              {formatBRL(total)}
            </strong>
          </div>

          {error && (
            <div className="text-danger" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={create.isPending || update.isPending}>
            Salvar
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
